import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  FormControl,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NgToastService } from 'ng-angular-popup';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { MatExpansionModule } from '@angular/material/expansion';
import { ApplicantPqqSectionsComponent } from '../applicant-pqq-sections.component';
import { environment } from '@/app/environments/environment';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DateFormatService } from '@/app/services/date-format.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { CountryService } from '@/app/services/shared/country';
import { GetFileIconUtilityService } from '@/app/services/utility/get-file-icon-utility.service';
import { FileManagerComponent } from '@/app/components/shared/file-manager/file-manager.component';

@Component({
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [
    MatExpansionModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatRadioModule,
    MatDatepickerModule,
    NgxCurrencyDirective,
    FileManagerComponent,
    MatSelectModule,
    MatOptionModule,
  ],
  selector: 'app-financial-information',
  templateUrl: './financial-information.component.html',
  styleUrls: ['./financial-information.component.css'],
})
export class FinancialInformationComponent implements OnInit, OnChanges {
  @Input() applicantData: any;
  @Input() isEdit: boolean;
  formGroup: FormGroup;
  years: number[] = [];
  base_path = environment.storageUrl;
  finRecentAuditAttach: any[] = [];
  oldFinRecentAuditAttach: any[] = [];
  country: any;
  currencySymbol: any;
  currency: any = 'USD';
  insuranceEmpAmountInWords: any;
  allocatedAmount: any;
  maxBudget = 100000000000;
  countryName: string;
  countries: any;

  deletedOldFinanAuditFilesIds: any[] = [];
  @Input() isFetchApplicantData: boolean;

  /**
   * NOTE: this is using the new files manager component. For old files,
   * they will be required to be updated using this component as well
   */
  oldInsuranceFiles: any[] = [];
  deletedOldInsuranceFilesIds: any[] = [];

  constructor(
    private fb: FormBuilder,
    private toast: NgToastService,
    private projectsService: ProjectsService,
    private ApplicantPqqSectionsComponent: ApplicantPqqSectionsComponent,
    private DateFormatService: DateFormatService,
    private countryServices: CountryService,
    private getFileIconService: GetFileIconUtilityService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isFetchApplicantData'] &&
      !changes['isFetchApplicantData'].firstChange
    ) {
      this.patchValuesInForm();
    }
    if (changes['this.applicantData']) {
      this.patchValuesInForm();
    }
    const currentYear = new Date().getFullYear();
    for (let year = currentYear - 9; year <= currentYear; year++) {
      this.years.push(year);
    }
  }
  ngOnInit(): void {
    this.formGroup = this.fb.group({
      // Financial Information
      annual_turnover_last_year_date: [''],
      annual_turnover_last_year_value: [null],
      annual_turnover_last_2_year_date: [''],
      annual_turnover_last_2_year_value: [null],
      annual_turnover_last_3_year_date: [''],
      annual_turnover_last_3_year_value: [null],

      has_faced_financial_difficulty: ['0'],
      // Insurance Cover (update to match new naming)

      insurance_employer_limit: [null],
      insurance_employer_expiry_date: [''],
      insurance_public_liability_limit: [null],
      insurance_public_liability_expiry_date: [''],
      insurance_professional_indemnity_limit: [null],
      insurance_professional_indemnity_expiry_date: [''],
      insurance_contract_works_limit: [null],
      insurance_contract_works_expiry_date: [''],

      insuranceFileControl: new FormArray([]),
    });
    this.patchValuesInForm();
    this.countryName = this.countryServices.getProjectCountryName();
    this.getCountriesList();
  }
  getCountriesList(): void {
    this.projectsService.getCountries().subscribe({
      next: (response: any) => {
        this.countries = response.data;
        this.countries.forEach((country) => {
          if (country.name === this.countryName) {
            this.currencySymbol = country.symbol;
          }
        });
      },
      error: (error) => {
        console.error('API error:', error);
      },
    });
  }
  numberToWords(num: any) {
    const belowTwenty = [
      '',
      'one',
      'two',
      'three',
      'four',
      'five',
      'six',
      'seven',
      'eight',
      'nine',
      'ten',
      'eleven',
      'twelve',
      'thirteen',
      'fourteen',
      'fifteen',
      'sixteen',
      'seventeen',
      'eighteen',
      'nineteen',
    ];
    const tens = [
      '',
      '',
      'twenty',
      'thirty',
      'forty',
      'fifty',
      'sixty',
      'seventy',
      'eighty',
      'ninety',
    ];
    const thousands = ['', 'thousand', 'million', 'billion', 'trillion'];

    function helper(n) {
      if (n === 0) return '';
      else if (n < 20) return belowTwenty[n] + ' ';
      else if (n < 100) return tens[Math.floor(n / 10)] + ' ' + helper(n % 10);
      else
        return belowTwenty[Math.floor(n / 100)] + ' hundred ' + helper(n % 100);
    }

    let word = '';
    let index = 0;

    while (num > 0) {
      if (num % 1000 !== 0) {
        word = helper(num % 1000) + thousands[index] + ' ' + word;
      }
      num = Math?.floor(num / 1000);
      index++;
    }

    return word.trim();
  }

  patchValuesInForm() {
    // If it's an edit scenario, patch the form with the applicant data
    if (this.isEdit) {
      this.formGroup.patchValue({
        annual_turnover_last_year_date: this.applicantData
          ?.annual_turnover_last_3_years
          ? +this.applicantData?.annual_turnover_last_3_years[0]?.year
          : '',
        annual_turnover_last_year_value: this.applicantData
          ?.annual_turnover_last_3_years
          ? this.applicantData?.annual_turnover_last_3_years[0]?.value
          : null,
        annual_turnover_last_2_year_date: this.applicantData
          ?.annual_turnover_last_3_years
          ? +this.applicantData?.annual_turnover_last_3_years[1]?.year
          : '',
        annual_turnover_last_2_year_value: this.applicantData
          ?.annual_turnover_last_3_years
          ? this.applicantData?.annual_turnover_last_3_years[1]?.value
          : null,
        annual_turnover_last_3_year_date: this.applicantData
          ?.annual_turnover_last_3_years
          ? +this.applicantData?.annual_turnover_last_3_years[2]?.year
          : '',
        annual_turnover_last_3_year_value: this.applicantData
          ?.annual_turnover_last_3_years
          ? this.applicantData?.annual_turnover_last_3_years[2]?.value
          : null,

        has_faced_financial_difficulty:
          String(this.applicantData?.has_faced_financial_difficulty) || '0',

        insurance_employer_limit:
          this.applicantData?.insurance_employer_limit || null,
        insurance_employer_expiry_date:
          this.applicantData?.insurance_employer_expiry_date || '',
        insurance_public_liability_limit:
          this.applicantData?.insurance_public_liability_limit || null,
        insurance_public_liability_expiry_date:
          this.applicantData?.insurance_public_liability_expiry_date || '',
        insurance_professional_indemnity_limit:
          this.applicantData?.insurance_professional_indemnity_limit || null,
        insurance_professional_indemnity_expiry_date:
          this.applicantData?.insurance_professional_indemnity_expiry_date ||
          '',
        insurance_contract_works_limit:
          this.applicantData?.insurance_contract_works_limit || null,
        insurance_contract_works_expiry_date:
          this.applicantData?.insurance_contract_works_expiry_date || '',
      });

      this.oldFinRecentAuditAttach =
        this.applicantData?.parent_document.filter(
          (doc) => doc.type === 'accounts_audit_report_attach'
        ) || [];

      this.addOldAttachmentMapping(this.applicantData);
    }
  }

  onFileUpload(event: any, name: string) {
    const files = event.target.files;

    if (name === 'fin_recent_audit_attach') {
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          this.finRecentAuditAttach.push(files[i]);
        }
      }
    }
  }

  removeNewFile(index: number, name: string) {
    if (name === 'fin_recent_audit_attach') {
      if (
        this.finRecentAuditAttach &&
        this.finRecentAuditAttach.length > index
      ) {
        this.finRecentAuditAttach.splice(index, 1);
      }
    }
  }

  removeOldFile(index: number, id: number, name: string) {
    if (name === 'fin_recent_audit_attach') {
      if (
        this.oldFinRecentAuditAttach &&
        this.oldFinRecentAuditAttach.length > index
      ) {
        this.oldFinRecentAuditAttach.splice(index, 1);
        this.deletedOldFinanAuditFilesIds.push(id);
      }
    }
  }

  openPdf(imageSrc: string): void {
    window.open(imageSrc, '_blank');
  }

  isImageFile(file: any) {
    // List of image file extensions
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    let fileExtension: any;
    // Extract the file extension from the file's name (not from preview)
    if (this.isEdit && !file?.name) {
      fileExtension = file?.split('.').pop()?.toLowerCase();
    } else if (this.isEdit && file?.name) {
      fileExtension = file?.name?.split('.').pop()?.toLowerCase();
    } else {
      fileExtension = file?.name?.split('.').pop()?.toLowerCase();
    }

    // Check if the extension is in the list of image extensions
    return imageExtensions.includes(fileExtension || '');
  }

  handleBack() {
    this.ApplicantPqqSectionsComponent.previousStep();
  }

  prepareDataToSubmit() {
    const dataToSubmit = new FormData();
    // Process the annual turnover for the last 3 years
    const annualTurnoverYears = [
      {
        year: this.formGroup.get('annual_turnover_last_3_year_date')?.value,
        value: this.formGroup.get('annual_turnover_last_3_year_value')?.value,
      },
      {
        year: this.formGroup.get('annual_turnover_last_2_year_date')?.value,
        value: this.formGroup.get('annual_turnover_last_2_year_value')?.value,
      },
      {
        year: this.formGroup.get('annual_turnover_last_year_date')?.value,
        value: this.formGroup.get('annual_turnover_last_year_value')?.value,
      },
    ];

    console.log('annualTurnoverYears:', annualTurnoverYears);

    // Append annual turnover years data in the required structure
    annualTurnoverYears.forEach((turnover, index) => {
      dataToSubmit.append(
        `annual_turnover_last_3_years[${index}][year]`,
        turnover.year
      );
      dataToSubmit.append(
        `annual_turnover_last_3_years[${index}][value]`,
        turnover.value
      );
    });

    // Append other financial difficulty and insurance data
    dataToSubmit.append(
      'has_faced_financial_difficulty',
      this.formGroup.get('has_faced_financial_difficulty')?.value
    );

    // Insurance Data
    dataToSubmit.append(
      'insurance_employer_limit',
      this.formGroup.get('insurance_employer_limit')?.value
    );
    dataToSubmit.append(
      'insurance_employer_expiry_date',
      this.DateFormatService.formatDate(
        this.formGroup.get('insurance_employer_expiry_date')?.value
      )
    );
    dataToSubmit.append(
      'insurance_public_liability_limit',
      this.formGroup.get('insurance_public_liability_limit')?.value
    );
    dataToSubmit.append(
      'insurance_public_liability_expiry_date',
      this.DateFormatService.formatDate(
        this.formGroup.get('insurance_public_liability_expiry_date')?.value
      )
    );
    dataToSubmit.append(
      'insurance_professional_indemnity_limit',
      this.formGroup.get('insurance_professional_indemnity_limit')?.value
    );
    dataToSubmit.append(
      'insurance_professional_indemnity_expiry_date',
      this.DateFormatService.formatDate(
        this.formGroup.get('insurance_professional_indemnity_expiry_date')
          ?.value
      )
    );
    dataToSubmit.append(
      'insurance_contract_works_limit',
      this.formGroup.get('insurance_contract_works_limit')?.value
    );
    dataToSubmit.append(
      'insurance_contract_works_expiry_date',
      this.DateFormatService.formatDate(
        this.formGroup.get('insurance_contract_works_expiry_date')?.value
      )
    );
    if (this.finRecentAuditAttach?.length) {
      this.finRecentAuditAttach.forEach((file: File) => {
        dataToSubmit.append('accounts_audit_report_attach[]', file);
      });
    }

    // Files handling Start
    this.addUploadedFiles(dataToSubmit);
    this.addDeletedOldFilesIds(dataToSubmit);
    // Files handling End

    return dataToSubmit;
  }

  addUploadedFiles(submissionFormData) {
    // Adding files to FormData
    this.formGroup.value.insuranceFileControl.forEach((uploadedFile: File) => {
      if (uploadedFile) {
        submissionFormData.append('insurance_attach[]', uploadedFile);
      }
    });
  }

  // NOTE: on submit, we append deleted files ids into data
  addDeletedOldFilesIds(submissionFormData) {
    if (this.deletedOldInsuranceFilesIds?.length > 0) {
      submissionFormData.append(
        'delete_insurance_attach_ids',
        JSON.stringify(this.deletedOldInsuranceFilesIds)
      );
    }
    if (this.deletedOldFinanAuditFilesIds?.length > 0) {
      submissionFormData.append(
        'delete_accounts_audit_report_attach_ids',
        JSON.stringify(this.deletedOldFinanAuditFilesIds)
      );
    }
  }

  onSubmit(): void {
    if (this.formGroup.valid) {
      const dataToSubmit = this.prepareDataToSubmit();
      this.projectsService.projectPQQResponseStep5(dataToSubmit).subscribe({
        next: () => {
          this.toast.success('Pre Qualification submitted', 'SUCCESS');
          this.ApplicantPqqSectionsComponent.nextStep();
        },
        error: (error: any) => {
          console.error('Failed to submit:', error);
          this.toast.danger(error.error.message, 'DANGER', 4000);
        },
      });
    } else {
      this.toast.warning('Please fill the required fields', 'WARNING');
      this.formGroup.markAllAsTouched(); // Highlights all invalid fields
    }
  }
  private getCurrencyInformation() {
    this.currencySymbol = this.countryServices
      .getCountries()
      .subscribe((country: any) => {
        this.country = country;
      });

    const data: any = localStorage.getItem('selectedCountry');
    const parseData: any = JSON.parse(data);
    this.currencySymbol = parseData?.symbol || '$';
    this.currency = parseData?.currency || 'USD';
  }
  getFileIcon(file: {
    file: File;
    preview: string;
    id?: number;
    name?: string;
  }): string {
    return this.getFileIconService.getFileIcon(file);
  }

  // NOTE: on edit, we add old files to the form array
  addOldAttachmentMapping(response) {
    if (response.insurance_attach) {
      this.oldInsuranceFiles = response.insurance_attach || [];
    }
  }

  /**
   * Handling File Operations - START
   */
  handleNewFiles(filesData: {
    files: File[];
    controlName: string;
    isEmpty: boolean;
  }) {
    switch (filesData.controlName) {
      case 'insuranceFileControl': {
        const fileArray = this.formGroup.get(
          'insuranceFileControl'
        ) as FormArray;
        fileArray.clear();

        // If no files are present, we break
        if (filesData.isEmpty) {
          break;
        }

        // Add new files to the form array
        filesData.files.forEach((file) =>
          fileArray.push(new FormControl(file))
        );
        break;
      }
      default:
        break;
    }
  }

  handleDeletedOldFiles(OldFileData: any) {
    switch (OldFileData.controlName) {
      case 'insuranceFileControl': {
        this.deletedOldInsuranceFilesIds.push(OldFileData?.file?.id);
        this.oldInsuranceFiles = this.oldInsuranceFiles.filter(
          (file) => file.id !== OldFileData?.file?.id
        );
        break;
      }
      default:
        break;
    }
  }
  /**
   * Handling File Operations - END
   */
}
